import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-story-details',
  templateUrl: './story-details.component.html',
  styleUrls: ['./story-details.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class StoryDetailsComponent implements OnInit {
  storyId!: number;
  story: any;
  loading = false;
  selectedStatus = '';
  rejectionReason = '';
  readonly defaultImage = 'assets/images/icons/logo.jpg';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apis: ApisService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.storyId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
  }

  load(): void {
    if (!this.storyId) return;
    this.loading = true;
    this.apis.getDashboardStoryDetails(this.storyId).subscribe({
      next: (res: any) => {
        this.story = res?.data || null;
        this.selectedStatus = this.story?.status || 'pending';
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.error('فشل تحميل تفاصيل القصة');
      }
    });
  }

  get writerId(): number | null {
    const id = this.story?.writer?.id || this.story?.user_id;
    return id ? Number(id) : null;
  }

  get writerName(): string {
    return this.story?.writer?.name || '-';
  }

  get coverUrl(): string {
    return this.normalizeImageUrl(this.story?.cover) || this.defaultImage;
  }

  get images(): any[] {
    return this.story?.images || [];
  }

  get likes(): any[] {
    return this.story?.likes?.users || [];
  }

  get comments(): any[] {
    return this.story?.comments?.items || [];
  }

  back(): void {
    this.router.navigate(['/stories']);
  }

  goToWriter(): void {
    if (!this.writerId) return;
    this.router.navigate(['/edit-user', this.writerId]);
  }

  sendNotificationToWriter(): void {
    if (!this.writerId) {
      this.toastr.info('لا يوجد كاتب مرتبط بهذه القصة');
      return;
    }
    this.router.navigate(['/send-notification', this.writerId]);
  }

  changeStatus(): void {
    if (!this.story?.id || !this.selectedStatus || this.selectedStatus === this.story.status) return;

    if (this.selectedStatus === 'rejected' && !this.rejectionReason.trim()) {
      const reason = window.prompt('اكتب سبب الرفض (اختياري):') || '';
      this.rejectionReason = reason;
    }

    this.loading = true;
    this.apis.updateDashboardStoryStatus(this.story.id, this.selectedStatus, this.rejectionReason).subscribe({
      next: () => {
        this.toastr.success('تم تحديث حالة القصة');
        this.rejectionReason = '';
        this.load();
      },
      error: () => {
        this.loading = false;
        this.toastr.error('فشل تحديث حالة القصة');
      }
    });
  }

  getStatusLabel(status: string): string {
    const key = (status || 'pending').toLowerCase();
    const map: any = {
      draft: 'مسودة',
      pending: 'بانتظار المراجعة',
      published: 'منشورة',
      rejected: 'مرفوضة',
      archived: 'مؤرشفة'
    };
    return map[key] || key;
  }

  getStatusClass(status: string): string {
    const key = (status || 'pending').toLowerCase();
    if (key === 'published') return 'badge-light-success';
    if (key === 'rejected') return 'badge-light-danger';
    if (key === 'archived') return 'badge-light-secondary';
    if (key === 'pending') return 'badge-light-warning';
    return 'badge-light-primary';
  }

  normalizeImageUrl(image: string | null | undefined): string {
    if (!image || typeof image !== 'string') return '';

    const value = image.trim().replace(/\\\//g, '/').replace(/^"|"$/g, '');
    if (!value) return '';
    if (/^(https?:|data:|blob:|assets\/)/i.test(value)) return value;
    if (value.startsWith('//')) return `https:${value}`;
    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(value)) return `https://${value}`;

    const apiOrigin = environment.apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
    if (value.startsWith('/')) return `${apiOrigin}${value}`;
    if (value.startsWith('storage/') || value.startsWith('AdminPanelAssetFiles/')) return `${apiOrigin}/${value}`;

    return `${apiOrigin}/storage/${value}`;
  }

  handleImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (!image || image.src.includes(this.defaultImage)) return;
    image.src = this.defaultImage;
  }
}
